import { useEffect, useRef, useState } from "react";

interface UseWakeLockReturn {
  /** True when the Wake Lock API is available in this browser. */
  supported: boolean;
  /** True when a wake lock is currently held. */
  active: boolean;
}

/**
 * Requests a `"screen"` Wake Lock while `active` is true. Re-acquires the lock
 * whenever the tab becomes visible again (wake locks are automatically released
 * when the page is hidden). Releases and does not re-acquire when `active` is
 * false or on unmount.
 *
 * SSR-safe: all `navigator` access is guarded by environment checks.
 * Never throws — any error from the Wake Lock API is silently ignored, and the
 * hook falls back to `supported: false`.
 */
export function useWakeLock(active: boolean): UseWakeLockReturn {
  const supported =
    typeof navigator !== "undefined" &&
    "wakeLock" in navigator &&
    typeof (navigator as { wakeLock?: unknown }).wakeLock === "object";

  const [isActive, setIsActive] = useState(false);
  const lockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!supported) return;
    if (!active) {
      // Release any held lock and clean up.
      void releaseLock();
      return;
    }

    async function acquireLock() {
      try {
        lockRef.current = await (navigator as Navigator & { wakeLock: WakeLock }).wakeLock.request(
          "screen",
        );
        setIsActive(true);

        lockRef.current.addEventListener("release", () => {
          setIsActive(false);
          lockRef.current = null;
        });
      } catch {
        // Permission denied or unsupported — degrade silently.
        setIsActive(false);
      }
    }

    async function releaseLock() {
      if (lockRef.current) {
        try {
          await lockRef.current.release();
        } catch {
          // Ignore errors on release.
        }
        lockRef.current = null;
      }
      setIsActive(false);
    }

    // Re-acquire on visibility change (the browser drops locks when hidden).
    function onVisibilityChange() {
      if (document.visibilityState === "visible" && active) {
        void acquireLock();
      }
    }

    void acquireLock();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      void releaseLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, supported]);

  return { supported, active: isActive };
}
